import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ProductDetail, ProductListItem } from '../../../../types/ProductTypes';
import { useDownloadFile } from '../../../../api/useDownloadFile';
import useProductQuery from '../../../../api/useProductQuery';
import useApi from '../../../../api/useApi';
import { useAppSettings } from '../../../../hooks/useAppSettings';
import { highlightMatch, sortByRelevance } from '../../../../utils/searchUtils';
import { useChartererStore, enrichWithChartererNames } from '../../../../context/ChartererContext';
import { SectionCard, SectionTitle } from '../../Section.styles';
import {
    USCGComparisonSection,
    USCGLogoSection,
    USCGSearchSection,
    SearchControls,
    USCGSearchInput,
    USCGCheckButton,
    CompatibilityResult,
    USCGDocumentsSection,
    DocumentLink,
    SearchInputContainer,
    TypeaheadDropdown,
    TypeaheadItem,
    TypeaheadNoResults,
} from './USCGCompatibilitySection.styles';

interface USCGCompatibilitySectionProps {
    product: ProductDetail;
}

const USCGCompatibilitySection: React.FC<USCGCompatibilitySectionProps> = ({ product }) => {
    const [compareProduct, setCompareProduct] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductListItem | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchResults, setSearchResults] = useState<ProductListItem[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(false);
    const [compatibilityResult, setCompatibilityResult] = useState<{
        status: 'compatible' | 'incompatible' | null;
        message: string;
        source?: string;
    }>({ status: null, message: 'Select a product to check compatibility' });

    const { downloadFile } = useDownloadFile();
    const { queryProducts } = useProductQuery();
    const { getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const { charterers } = useChartererStore();
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const queryProductsRef = useRef(queryProducts);
    const charterersRef = useRef(charterers);

    // Keep refs up to date
    useEffect(() => {
        queryProductsRef.current = queryProducts;
    }, [queryProducts]);
    useEffect(() => {
        charterersRef.current = charterers;
    }, [charterers]);

    // Minimum characters before showing typeahead
    const MIN_SEARCH_LENGTH = 3;
    const SEARCH_DEBOUNCE_MS = 300;

    // Search products from API with debounce
    const searchProductsDebounced = useCallback((searchTerm: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm.length < MIN_SEARCH_LENGTH) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        setIsSearching(true);
        setShowDropdown(true); // Show dropdown immediately with loading state
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                console.log('[USCG] Searching for:', searchTerm);
                const result = await queryProductsRef.current({
                    search: searchTerm,
                    pageSize: 50,
                    includeInactive: false,
                });
                console.log('[USCG] Search result:', result);
                // Filter out default (category-level) products and apply relevance sorting
                const enriched = enrichWithChartererNames(result.items, charterersRef.current);
                const filtered = enriched.filter((item) => !!item.tradeName);
                const sorted = sortByRelevance(filtered, searchTerm);
                setSearchResults(sorted.slice(0, 10));
            } catch (error) {
                console.error('[USCG] Error searching products:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    }, []); // No dependencies - uses ref

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCompareProduct(value);
        setSelectedProduct(null);
        setHighlightedIndex(-1);
        setCompatibilityResult({ status: null, message: 'Select a product to check compatibility' });
        searchProductsDebounced(value);
    };

    const handleSelectProduct = (selectedItem: ProductListItem) => {
        setCompareProduct(selectedItem.tradeName || selectedItem.categoryName || '');
        setSelectedProduct(selectedItem);
        setShowDropdown(false);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown || searchResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                    handleSelectProduct(searchResults[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    const currentProductHasUscgGroup = Boolean(product.regulations?.uscgGroup);
    const isDefaultProduct = !product.tradeName;

    const handleCheckCompatibility = async () => {
        if (!selectedProduct) {
            setCompatibilityResult({
                status: null,
                message: 'Please select a product to compare',
            });
            return;
        }

        if (!currentProductHasUscgGroup) {
            setCompatibilityResult({
                status: null,
                message: `${product.tradeName || product.category?.name || 'This product'} does not have a USCG group assigned`,
            });
            return;
        }

        const apiUrl = appSettings?.api;
        if (!apiUrl) {
            setCompatibilityResult({
                status: null,
                message: 'API not configured',
            });
            return;
        }

        setIsCheckingCompatibility(true);
        setCompatibilityResult({
            status: null,
            message: 'Checking compatibility...',
        });

        try {
            // Call the USCG inter-product compatibility API
            const endpoint = `${apiUrl}GetUscgInterProductCompatibility?id1=${product.id}&id2=${selectedProduct.id}`;
            console.log('[USCG] Checking compatibility:', endpoint);

            const response = await getApiData({ endpoint });
            console.log('[USCG] Compatibility response:', response);

            // Response shape: { areCompatible: boolean, compatibilitySource: number }
            // compatibilitySource: 0 = InterGroupCompatibility, 1 = ProductToGroupException, 2 = ProductToProductException
            if (response) {
                const areCompatible = response.areCompatible ?? response.AreCompatible;
                const compatibilitySource = response.compatibilitySource ?? response.CompatibilitySource ?? response.USCGCompatibilitySource;

                const productName = product.tradeName || product.category?.name || 'Unknown Product';
                const compareProductName = selectedProduct.tradeName ?? selectedProduct.categoryName ?? 'Unknown Product';
                const status: 'compatible' | 'incompatible' = areCompatible ? 'compatible' : 'incompatible';
                const message = areCompatible
                    ? `${productName} and ${compareProductName} are Compatible`
                    : `${productName} and ${compareProductName} are Not Compatible`;

                let source = '';
                switch (compatibilitySource) {
                    case 0:
                        source = 'This result is based on the USCG compatibility chart. No exceptions have been found.';
                        break;
                    case 1:
                        source = 'This result is based on the USCG group exception list.';
                        break;
                    case 2:
                        source = 'This result is based on the USCG product exception list.';
                        break;
                }

                setCompatibilityResult({
                    status,
                    message,
                    source,
                });
            } else {
                setCompatibilityResult({
                    status: null,
                    message: 'No compatibility data found',
                });
            }
        } catch (error: any) {
            console.error('[USCG] Error checking compatibility:', error);

            // Extract error message from API response
            const apiErrorMessage = error.response?.data?.message || error.response?.data || error.message;

            // Check if the error is about missing USCG data
            let userMessage = 'Failed to check compatibility';
            if (typeof apiErrorMessage === 'string') {
                if (
                    apiErrorMessage.toLowerCase().includes('uscg group') ||
                    apiErrorMessage.toLowerCase().includes('not exist') ||
                    apiErrorMessage.toLowerCase().includes('not found')
                ) {
                    userMessage = `One or both products do not have a USCG group assigned. Compatibility cannot be determined.`;
                } else {
                    userMessage = apiErrorMessage;
                }
            }

            setCompatibilityResult({
                status: null,
                message: userMessage,
            });
        } finally {
            setIsCheckingCompatibility(false);
        }
    };

    return (
        <SectionCard>
            <SectionTitle>USCG Compatibility</SectionTitle>

            {/* Product Comparison Tool - only for charterer-specific products */}
            {!isDefaultProduct && (
            <USCGComparisonSection>
                <USCGSearchSection>
                    <label>Product to be compared with:</label>
                    <SearchControls>
                        <SearchInputContainer>
                            <USCGSearchInput
                                ref={inputRef}
                                type="text"
                                placeholder="Search for product (min 3 characters)..."
                                value={compareProduct}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => {
                                    if (compareProduct && compareProduct.length >= MIN_SEARCH_LENGTH && searchResults && searchResults.length > 0) {
                                        setShowDropdown(true);
                                    }
                                }}
                            />
                            {showDropdown && (
                                <TypeaheadDropdown ref={dropdownRef}>
                                    {isSearching ? (
                                        <TypeaheadNoResults>Searching...</TypeaheadNoResults>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map((item, index) => (
                                            <TypeaheadItem
                                                key={item.id}
                                                isHighlighted={index === highlightedIndex}
                                                onMouseDown={(e) => {
                                                    e.preventDefault(); // Prevent input blur/clickOutside from closing dropdown
                                                    handleSelectProduct(item);
                                                }}
                                                onMouseEnter={() => setHighlightedIndex(index)}
                                            >
                                                <span className="product-info">
                                                    <span className="product-name">{highlightMatch(item.tradeName || item.categoryName || '', compareProduct)}</span>
                                                    {item.categoryName && <span className="product-category">{highlightMatch(item.categoryName, compareProduct)}</span>}
                                                </span>
                                                {item.chartererName && <span className="product-charterer">{highlightMatch(item.chartererName, compareProduct)}</span>}
                                            </TypeaheadItem>
                                        ))
                                    ) : (
                                        <TypeaheadNoResults>No products found</TypeaheadNoResults>
                                    )}
                                </TypeaheadDropdown>
                            )}
                        </SearchInputContainer>
                        <USCGCheckButton onClick={handleCheckCompatibility} disabled={!selectedProduct || isCheckingCompatibility}>
                            {isCheckingCompatibility ? 'Checking...' : 'Check Compatibility'}
                        </USCGCheckButton>
                    </SearchControls>
                </USCGSearchSection>
                <USCGLogoSection>
                    <img src="/assets/images/uscg2.jpg" alt="USCG" />
                </USCGLogoSection>
            </USCGComparisonSection>
            )}
            {!isDefaultProduct && (
            <CompatibilityResult className={compatibilityResult.status || ''}>
                <h2>{compatibilityResult.message}</h2>
                {compatibilityResult.source && <p>{compatibilityResult.source}</p>}
            </CompatibilityResult>
            )}

            {/* USCG Documents */}
            <USCGDocumentsSection>
                <DocumentLink>
                    <img src="/assets/images/USCG compatibility chart icon.png" alt="USCG compatibility chart" />
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            console.log('[USCG] Downloading USCG Compatibility Chart...');
                            downloadFile({ category: 'uscg', fileName: 'USCG Compatibility Chart with comments.xls' });
                        }}
                    >
                        USCG Compatibility Chart
                    </a>
                </DocumentLink>
                <DocumentLink>
                    <img src="/assets/images/USCG CFR Title 46 Part 150 icon.png" alt="CFR Title 46 Part 150" />
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            console.log('[USCG] Downloading CFR Title 46...');
                            downloadFile({ category: 'uscg', fileName: 'CFR Title 46 Part 150 - Compatibility of Cargoes.pdf' });
                        }}
                    >
                        CFR Title 46 Part 150 - Compatibility of Cargoes
                    </a>
                </DocumentLink>
            </USCGDocumentsSection>

            {/* Exceptions Section (Edit mode only - placeholder for now) */}
            {/* This would only show in edit mode
            <ExceptionsSection>
                <h3>Exceptions to the Compatibility Chart</h3>
                
                <ExceptionsTableContainer>
                    <h4>USCG Group Exceptions</h4>
                    <USCGTable>
                        <div className="table-header exceptions-header">
                            <span>USCG Group</span>
                            <span>Compatibility</span>
                        </div>
                        {uscgGroupExceptions.map((exception, index) => (
                            <div className="table-row exceptions-row" key={index}>
                                <span>{exception.uscgGroup.number} - {exception.uscgGroup.name}</span>
                                <span>{exception.compatibility}</span>
                            </div>
                        ))}
                    </USCGTable>
                </ExceptionsTableContainer>

                <ExceptionsTableContainer>
                    <h4>Inter-Product Exceptions</h4>
                    <USCGTable>
                        <div className="table-header inter-products-header">
                            <span>Product</span>
                            <span>USCG Group</span>
                            <span>Compatibility</span>
                        </div>
                        {interProductExceptions.map((exception, index) => (
                            <div className="table-row inter-products-row" key={index}>
                                <a href={`/product/${exception.productId}`}>{exception.productName}</a>
                                <span>{exception.uscgGroupNumber}</span>
                                <span>{exception.compatibility}</span>
                            </div>
                        ))}
                    </USCGTable>
                </ExceptionsTableContainer>
            </ExceptionsSection>
            */}
        </SectionCard>
    );
};

export default USCGCompatibilitySection;
